import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import { UsersRound, Plus, Search, Loader2, FileDown, Upload, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { colaboradoresAPI } from '../../lib/api';
import usePermissions from '../../hooks/usePermissions';
import ColaboradorFormDialog from './ColaboradorFormDialog';
import ColaboradorDetailDialog from './ColaboradorDetailDialog';
import ColaboradorImportDialog from './ColaboradorImportDialog';

const FILTERS = [
  { key: 'activos', label: 'Activos' },
  { key: 'bajas', label: 'Bajas' },
  { key: 'todos', label: 'Todos' },
];

export default function Colaboradores() {
  const { canWrite } = usePermissions();
  const canFull = canWrite('capital_humano', 'colaboradores');

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [estado, setEstado] = useState('activos');

  const [detail, setDetail] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formColab, setFormColab] = useState(null);

  // Baja / Reingreso dialogs
  const [bajaTarget, setBajaTarget] = useState(null);
  const [bajaForm, setBajaForm] = useState({ fecha_baja: '', causa_baja: '' });
  const [reingresoTarget, setReingresoTarget] = useState(null);
  const [reingresoForm, setReingresoForm] = useState({ clasificacion: '', departamento: '', puesto: '', fecha_alta: '', salario_diario: '', sueldo: '', sd_fiscal: '', temporalidad_contrato: '', fecha_termino_contrato: '', estatus_colaborador: 'Activo' });
  const [actionSaving, setActionSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await colaboradoresAPI.downloadTemplate();
      downloadBlob(res.data, 'plantilla_colaboradores.xlsx');
      toast.success('Plantilla descargada');
    } catch {
      toast.error('No se pudo descargar la plantilla');
    }
  };

  const handleExportFull = async () => {
    setExporting(true);
    try {
      const res = await colaboradoresAPI.exportFull();
      downloadBlob(res.data, `colaboradores_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success('Exportación lista', {
        description: 'Este archivo es para consulta/respaldo. Si se reimporta, los colaboradores existentes no se actualizarán.',
      });
    } catch {
      toast.error('No se pudo exportar');
    } finally {
      setExporting(false);
    }
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await colaboradoresAPI.getAll({ search: search || undefined, estado });
      setList(res.data || []);
    } catch (err) {
      toast.error('Error al cargar colaboradores');
    } finally {
      setLoading(false);
    }
  }, [search, estado]);

  useEffect(() => {
    const t = setTimeout(fetchList, 300);
    return () => clearTimeout(t);
  }, [fetchList]);

  const openDetail = async (id) => {
    try {
      const res = await colaboradoresAPI.getById(id);
      setDetail(res.data);
    } catch {
      toast.error('No se pudo cargar la ficha');
    }
  };

  const handleSaved = () => {
    setFormOpen(false);
    setFormColab(null);
    fetchList();
    if (detail) openDetail(detail.id);
  };

  const submitBaja = async () => {
    if (!bajaForm.fecha_baja || !bajaForm.causa_baja.trim()) {
      toast.error('Fecha de baja y causa son obligatorias');
      return;
    }
    setActionSaving(true);
    try {
      await colaboradoresAPI.baja(bajaTarget.id, bajaForm);
      toast.success('Colaborador dado de baja');
      setBajaTarget(null);
      setBajaForm({ fecha_baja: '', causa_baja: '' });
      setDetail(null);
      fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al dar de baja');
    } finally {
      setActionSaving(false);
    }
  };

  const submitReingreso = async () => {
    if (!reingresoForm.departamento.trim() || !reingresoForm.puesto.trim() || !reingresoForm.fecha_alta) {
      toast.error('Departamento, puesto y fecha de alta son obligatorios');
      return;
    }
    setActionSaving(true);
    try {
      await colaboradoresAPI.reingreso(reingresoTarget.id, {
        clasificacion: reingresoForm.clasificacion || null,
        departamento: reingresoForm.departamento,
        puesto: reingresoForm.puesto,
        fecha_alta: reingresoForm.fecha_alta,
        salario_diario: reingresoForm.salario_diario === '' ? null : parseFloat(reingresoForm.salario_diario),
        sueldo: reingresoForm.sueldo === '' ? null : parseFloat(reingresoForm.sueldo),
        sd_fiscal: reingresoForm.sd_fiscal === '' ? null : parseFloat(reingresoForm.sd_fiscal),
        temporalidad_contrato: reingresoForm.temporalidad_contrato || null,
        fecha_termino_contrato: reingresoForm.fecha_termino_contrato || null,
        estatus_colaborador: reingresoForm.estatus_colaborador || 'Activo',
      });
      toast.success('Reingreso registrado');
      setReingresoTarget(null);
      setReingresoForm({ clasificacion: '', departamento: '', puesto: '', fecha_alta: '', salario_diario: '', sueldo: '', sd_fiscal: '', temporalidad_contrato: '', fecha_termino_contrato: '', estatus_colaborador: 'Activo' });
      setDetail(null);
      fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error en el reingreso');
    } finally {
      setActionSaving(false);
    }
  };

  const activePeriod = (c) => {
    const p = c.periodos_laborales || [];
    return p.length && !p[p.length - 1].fecha_baja ? p[p.length - 1] : (p[p.length - 1] || {});
  };

  return (
    <Layout>
      <div data-testid="colaboradores-page" className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-slate-900 flex items-center justify-center">
              <UsersRound className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">Colaboradores</h1>
              <p className="text-slate-500 text-sm">Capital Humano</p>
            </div>
          </div>
          {canFull && (
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" onClick={handleDownloadTemplate} data-testid="colab-template-btn">
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Descargar plantilla
              </Button>
              <Button variant="outline" onClick={handleExportFull} disabled={exporting} data-testid="colab-export-btn">
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />} Exportar completo
              </Button>
              <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="colab-import-btn">
                <Upload className="w-4 h-4 mr-2" /> Importar
              </Button>
              <Button onClick={() => { setFormColab(null); setFormOpen(true); }} data-testid="colab-new-btn">
                <Plus className="w-4 h-4 mr-2" /> Nuevo colaborador
              </Button>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              placeholder="Buscar por nombre, nómina o CURP…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="colab-search"
            />
          </div>
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setEstado(f.key)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${estado === f.key ? 'bg-white shadow text-slate-900 font-medium' : 'text-slate-500'}`}
                data-testid={`colab-filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
          <table className="w-full text-sm" data-testid="colab-table">
            <thead className="bg-slate-50 text-slate-500">
              <tr className="text-left">
                <th className="px-4 py-3 font-medium">Nómina</th>
                <th className="px-4 py-3 font-medium">Nombre completo</th>
                <th className="px-4 py-3 font-medium">Departamento</th>
                <th className="px-4 py-3 font-medium">Puesto</th>
                <th className="px-4 py-3 font-medium">Estado</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
              ) : list.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400" data-testid="colab-empty">Sin colaboradores para mostrar.</td></tr>
              ) : (
                list.map((c) => {
                  const ap = activePeriod(c);
                  return (
                    <tr
                      key={c.id}
                      className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                      onClick={() => openDetail(c.id)}
                      data-testid={`colab-row-${c.numero_nomina}`}
                    >
                      <td className="px-4 py-3 font-medium text-slate-700">{c.numero_nomina}</td>
                      <td className="px-4 py-3 text-slate-800">{c.nombre_completo}</td>
                      <td className="px-4 py-3 text-slate-600">{ap.departamento || '—'}</td>
                      <td className="px-4 py-3 text-slate-600">{ap.puesto || '—'}</td>
                      <td className="px-4 py-3">
                        <Badge className={c.estado === 'activo' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}>
                          {c.estado === 'activo' ? 'Activo' : 'Baja'}
                        </Badge>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Form dialog */}
      <ColaboradorFormDialog
        open={formOpen}
        colaborador={formColab}
        onClose={() => { setFormOpen(false); setFormColab(null); }}
        onSaved={handleSaved}
      />

      {/* Import dialog */}
      <ColaboradorImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={fetchList}
      />

      {/* Detail dialog */}
      <ColaboradorDetailDialog
        open={!!detail}
        colaborador={detail}
        canFull={canFull}
        onClose={() => setDetail(null)}
        onEdit={(c) => { setDetail(null); setFormColab(c); setFormOpen(true); }}
        onBaja={(c) => { setDetail(null); setBajaTarget(c); }}
        onReingreso={(c) => { setDetail(null); setReingresoTarget(c); }}
      />

      {/* Baja dialog */}
      <Dialog open={!!bajaTarget} onOpenChange={(o) => !o && setBajaTarget(null)}>
        <DialogContent data-testid="colab-baja-dialog">
          <DialogHeader><DialogTitle>Dar de baja</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-600">Fecha de baja <span className="text-red-500">*</span></Label>
              <Input type="date" value={bajaForm.fecha_baja} onChange={(e) => setBajaForm((p) => ({ ...p, fecha_baja: e.target.value }))} data-testid="colab-baja-fecha" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Causa de baja <span className="text-red-500">*</span></Label>
              <Textarea value={bajaForm.causa_baja} onChange={(e) => setBajaForm((p) => ({ ...p, causa_baja: e.target.value }))} rows={2} data-testid="colab-baja-causa" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBajaTarget(null)} disabled={actionSaving}>Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={submitBaja} disabled={actionSaving} data-testid="colab-baja-submit">
              {actionSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Confirmar baja
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reingreso dialog */}
      <Dialog open={!!reingresoTarget} onOpenChange={(o) => !o && setReingresoTarget(null)}>
        <DialogContent data-testid="colab-reingreso-dialog">
          <DialogHeader><DialogTitle>Reingreso</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs text-slate-600">Clasificación</Label><Input value={reingresoForm.clasificacion} onChange={(e) => setReingresoForm((p) => ({ ...p, clasificacion: e.target.value }))} data-testid="colab-reingreso-clasificacion" /></div>
            <div><Label className="text-xs text-slate-600">Departamento <span className="text-red-500">*</span></Label><Input value={reingresoForm.departamento} onChange={(e) => setReingresoForm((p) => ({ ...p, departamento: e.target.value }))} data-testid="colab-reingreso-departamento" /></div>
            <div><Label className="text-xs text-slate-600">Puesto <span className="text-red-500">*</span></Label><Input value={reingresoForm.puesto} onChange={(e) => setReingresoForm((p) => ({ ...p, puesto: e.target.value }))} data-testid="colab-reingreso-puesto" /></div>
            <div><Label className="text-xs text-slate-600">Fecha de alta <span className="text-red-500">*</span></Label><Input type="date" value={reingresoForm.fecha_alta} onChange={(e) => setReingresoForm((p) => ({ ...p, fecha_alta: e.target.value }))} data-testid="colab-reingreso-fecha" /></div>
            <div><Label className="text-xs text-slate-600">Salario diario (MXN)</Label><Input type="number" value={reingresoForm.salario_diario} onChange={(e) => setReingresoForm((p) => ({ ...p, salario_diario: e.target.value }))} data-testid="colab-reingreso-salario" /></div>
            <div><Label className="text-xs text-slate-600">Sueldo mensual (MXN)</Label><Input type="number" value={reingresoForm.sueldo} onChange={(e) => setReingresoForm((p) => ({ ...p, sueldo: e.target.value }))} data-testid="colab-reingreso-sueldo" /></div>
            <div><Label className="text-xs text-slate-600">SD fiscal (MXN)</Label><Input type="number" value={reingresoForm.sd_fiscal} onChange={(e) => setReingresoForm((p) => ({ ...p, sd_fiscal: e.target.value }))} data-testid="colab-reingreso-sdfiscal" /></div>
            <div><Label className="text-xs text-slate-600">Temporalidad de contrato</Label><Input value={reingresoForm.temporalidad_contrato} onChange={(e) => setReingresoForm((p) => ({ ...p, temporalidad_contrato: e.target.value }))} data-testid="colab-reingreso-temporalidad" /></div>
            <div><Label className="text-xs text-slate-600">Fecha término contrato</Label><Input type="date" value={reingresoForm.fecha_termino_contrato} onChange={(e) => setReingresoForm((p) => ({ ...p, fecha_termino_contrato: e.target.value }))} data-testid="colab-reingreso-ftermino" /></div>
            <div>
              <Label className="text-xs text-slate-600">Estatus colaborador</Label>
              <Select value={reingresoForm.estatus_colaborador} onValueChange={(v) => setReingresoForm((p) => ({ ...p, estatus_colaborador: v }))}>
                <SelectTrigger data-testid="colab-reingreso-estatus"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['Prueba', 'Activo', 'Incapacidad', 'Inactivo'].map((op) => <SelectItem key={op} value={op}>{op}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReingresoTarget(null)} disabled={actionSaving}>Cancelar</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={submitReingreso} disabled={actionSaving} data-testid="colab-reingreso-submit">
              {actionSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Confirmar reingreso
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
